import {
  Box,
  Theme,
  ThemeProvider,
  Typography,
  useMediaQuery,
} from "@mui/material";
import styled from "@emotion/styled";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "@mui/material/Skeleton";
import { createTheme } from "@mui/material/styles";
import ArrowDown from "@icons/myicons/arrow-down.svg?react";
import LogoutIcon from "@icons/myicons/sidebar/logout.svg?react";
import { UserProfilesApiProfileImageRequest } from "@rentablo/api/apiv1";
import { getLetters } from "@helpers/getLetters";
import { useMyself } from "@api/v1/hooks/useMyself";
import { useAppLayout } from "@providers/AppLayoutProvider";
import { useGetUserProfileImage } from "@features/settings/useSettings";
import { createObjectUrl } from "@helpers/files";
import { useNavigate } from "react-router";
import ProfileIcon from "@icons/myicons/user-1.svg?react";
import { MenuItem, MenuItemExpandable } from "@components/common";
import { HelpdeskMenuItem } from "./HelpdeskMenuItem";

const ArrowRight = styled(ArrowDown)`
  width: 20px;
  height: 20px;
  transform: rotate(-90deg);
  color: ${({ theme }) => theme.palette.sidebar.textOnSidebar};
`;

const Ava = styled(Box)`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100px;
  background-color: ${({ theme }) => theme.palette.sidebar.avatarOnsidebar};
  color: ${({ theme }) => theme.palette.button.primaryButton.textOnDefault};
`;

const Name = styled(Typography)`
  width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  color: ${({ theme }) => theme.palette.sidebar.sidebarColorOnDark};
`;

const Email = styled(Typography)`
  width: 100%;
  font-size: 12px;
  color: ${({ theme }) => theme.palette.sidebar.textOnSidebar};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const StyledImage = styled("img")({
  width: 40,
  height: 40,
  borderRadius: "50%",
  objectFit: "cover",
});

const darkTheme = createTheme({
  shape: { borderRadius: 6 },
  // @ts-ignore
  palette: {
    mode: "dark",
  },
});

export const Profile = () => {
  const { data } = useMyself();
  const matches = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [profileImage, setProfileImage] = useState("");
  const userId = data?.profileReference?.id;
  useGetUserProfileImage({ id: userId } as UserProfilesApiProfileImageRequest, {
    enabled: Boolean(userId),
    onSuccess: (data) => {
      if (data?.size > 4) {
        setProfileImage(createObjectUrl(data));
      }
    },
  });
  const { t } = useTranslation();
  const { sidebarCollapsed: collapsed } = useAppLayout();

  const NameEl = useMemo(
    () => (
      <Box
        display="flex"
        alignItems="flex-start"
        flexDirection="column"
        minWidth={0}
        width="100%"
      >
        {!collapsed && (
          <>
            {data?.profileReference?.firstName &&
            data?.profileReference?.lastName ? (
              <Name variant="body3">
                {data?.profileReference.firstName}{" "}
                {data?.profileReference.lastName}
              </Name>
            ) : (
              <ThemeProvider theme={darkTheme}>
                <Skeleton variant="text" width="100%" />
              </ThemeProvider>
            )}

            {data?.email ? (
              <Email variant="caption">{data?.email}</Email>
            ) : (
              <ThemeProvider theme={darkTheme}>
                <Skeleton variant="text" width="100%" />
              </ThemeProvider>
            )}
          </>
        )}
      </Box>
    ),
    [collapsed, data]
  );

  if (matches)
    return (
      <MenuItemExpandable
        bigIcon
        before={
          profileImage ? (
            <StyledImage src={profileImage} />
          ) : (
            <Ava>{`${getLetters(data)[0]}${getLetters(data)[1]}`}</Ava>
          )
        }
        name={NameEl}
        selected={false}
        expandIcon={<ArrowDown fontSize="inherit" />}
      >
        <MenuItem
          selected={false}
          to="/settings/profile"
          before={
            <Box pl={0.5} display="flex" alignItems="center">
              <ProfileIcon width={22} height={22} />
            </Box>
          }
          name={t("appSidebar.menu.profile")}
          state={{ from: location.pathname }}
        />
        <HelpdeskMenuItem />
        <MenuItem
          selected={false}
          to="/logout"
          name={t("appSidebar.menu.logout")}
          before={<LogoutIcon />}
        />
      </MenuItemExpandable>
    );

  return (
    <Box
      sx={{
        cursor: "pointer",
        "&:hover": {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? theme.palette.sidebar.hover
              : theme.palette.sidebar.hover,
        },
      }}
      onClick={() => navigate("/settings/profile")}
      display="flex"
      alignItems="center"
      gap={4}
      mt={collapsed ? 0 : 1.5}
      mb={2}
      py={2}
      pl={collapsed ? 0 : 2}
      pr={1}
      borderRadius={1}
    >
      <Box flexShrink={0}>
        {profileImage ? (
          <StyledImage src={profileImage} />
        ) : (
          <Ava>{`${getLetters(data)[0]}${getLetters(data)[1]}`}</Ava>
        )}
      </Box>
      {NameEl}
      <Box flexShrink={0} display="flex" alignItems="center">
        <ArrowRight />
      </Box>
    </Box>
  );
};
